import { CropFieldAdvice, SprayAdvice } from '../models/models';

export type CropFieldComparator = (a: CropFieldAdvice, b: CropFieldAdvice) => number;

export enum SortMethodType {
  Name = 'name',
  Advice = 'advice',
  Spray = 'spray',
}

/**
 * The default sorting order.
 * Sort fields based on name (alphabetical order).
 */
export const compareCropFieldByName: CropFieldComparator = (a, b) =>
  a.cropFieldName.localeCompare(b.cropFieldName);

/**
 * Sort fields based on advice for the current date.
 *
 * - Fields that need a stop spray today should be on top.
 * - Fields that need curative spray today should be second from top.
 * - Fields that need preventive spray today should be third from top.
 * - Fields that do not need any spray today should be at bottom.
 */
export const compareCropFieldByAdvice: CropFieldComparator = (a: CropFieldAdvice, b: CropFieldAdvice) => {
  const adviceA = a.todaysAdvice?.sprayAdvice ?? SprayAdvice.noAdvice;
  const adviceB = b.todaysAdvice?.sprayAdvice ?? SprayAdvice.noAdvice;

  const sprayPriority = {
    [SprayAdvice.stop]: 0,
    [SprayAdvice.curative]: 1,
    [SprayAdvice.preventive]: 2,
    [SprayAdvice.cropsProtected]: 3,
    [SprayAdvice.noAdvice]: 4,
  };

  if (adviceA === adviceB) {
    return compareCropFieldByName(a, b);
  }

  return sprayPriority[adviceA] - sprayPriority[adviceB];
};

/**
 * Sort fields based on latest spray date.
 *
 * - The field that has not been sprayed the longest should be at the top.
 * - The field that has been sprayed most recently should be at the bottom.
 */
export const compareCropFieldByLastSprayDate: CropFieldComparator = (a, b) => {
  const lastSprayDateA = a.lastSprayDate ?? new Date(0);
  const lastSprayDateB = b.lastSprayDate ?? new Date(0);

  if (lastSprayDateA.getTime() === lastSprayDateB.getTime()) {
    return compareCropFieldByName(a, b);
  }

  return lastSprayDateA.getTime() - lastSprayDateB.getTime();
};
